import { useMemo } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import { Flex, Text } from '../../../Components'
import {
  CHART_ACTIVE_DOT,
  CHART_ANIMATION,
  CHART_ANIMATION_FAST,
  CHART_AXIS_LINE,
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_LEGEND_STYLE,
  CHART_MARGIN,
  CHART_TOOLTIP_STYLE
} from '../../../Components/Charts/chartTheme'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { formatDayKeyLabel, formatMs, getChartColor } from './utils'

const language = cap.language.state || 'IT'
const t = translations[language]

/**
 * Costruisce le righe del grafico combinato (conteggi giornalieri + medie per unit con σ).
 * @param {Array<{ bucket: string|Date, unit: string, count: number, avg: number, stdDev?: number }>} series - Punti API bucket giornaliero
 * @param {string[]} units - Unit selezionate
 * @returns {Array<Record<string, unknown>>} Righe ordinate per ComposedChart
 */
function buildDailyComboRows(series, units) {
  const unitSet = new Set(units)
  const byDay = {}

  for (const point of series) {
    if (!unitSet.has(point.unit)) continue
    const dayKey =
      typeof point.bucket === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(point.bucket)
        ? point.bucket
        : new Date(point.bucket).toISOString().slice(0, 10)

    if (!byDay[dayKey]) {
      byDay[dayKey] = {
        nameSort: dayKey,
        name: formatDayKeyLabel(dayKey),
        totalCount: 0
      }
    }
    const row = byDay[dayKey]
    row.totalCount += point.count || 0
    const std = Number.isFinite(point.stdDev) ? point.stdDev : 0
    row[`${point.unit}_avg`] = point.avg
    row[`${point.unit}_std`] = Math.max(0, std)
  }

  return Object.values(byDay).sort((a, b) => a.nameSort.localeCompare(b.nameSort))
}

/**
 * Tooltip personalizzato: conteggio giornaliero e media ± deviazione standard per unit
 * @param {Object} props - Props Recharts Tooltip
 * @param {boolean} props.active - Se il tooltip e' visibile
 * @param {Array} props.payload - Serie sotto il cursore
 * @param {string[]} units - Unit selezionate
 * @returns {JSX.Element|null}
 */
function DailyComboTooltip({ active, payload, units }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  return (
    <div style={{ ...CHART_TOOLTIP_STYLE, padding: '10px 14px', lineHeight: 1.6 }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--ink-900)' }}>{row.name}</div>
      <div style={{ marginBottom: 4, color: 'var(--ink-500)' }}>
        {t.pfEsecuzioniSommaUnit}{' '}
        <strong style={{ color: 'var(--ink-900)' }}>{row.totalCount}</strong>
      </div>
      {units.map((u, i) => {
        const avg = row[`${u}_avg`]
        if (avg == null) return null
        const std = row[`${u}_std`] ?? 0
        const color = getChartColor(i)
        return (
          <div key={u} style={{ marginTop: 4, color }}>
            <span style={{ fontWeight: 600 }}>{u}</span>: {t.pfMedia} {formatMs(avg)} · σ{' '}
            {formatMs(std)}{' '}
            <span style={{ color: 'var(--ink-400)' }}>
              [{formatMs(Math.max(0, avg - std))} — {formatMs(avg + std)}]
            </span>
          </div>
        )
      })}
    </div>
  )
}

export const DailyPerformanceComboChart = ({ startDate, endDate, selectedUnits = [] }) => {
  const { data: timeSeries = [], isSuccess } = useCQuery([
    'unitPerformanceTimeSeries',
    startDate,
    endDate,
    selectedUnits.join(','),
    'day'
  ])

  const chartData = useMemo(
    () => buildDailyComboRows(timeSeries, selectedUnits),
    [timeSeries, selectedUnits]
  )

  if (!isSuccess) return null

  if (!selectedUnits.length) {
    return (
      <div className="ncard" style={{ marginTop: 16 }}>
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-chart-line" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.pfAndamentoGiornaliero}</h3>
            <div className="ncard__sub">{t.pfSelezionaUnitTabella}</div>
          </div>
        </div>
        <div
          className="ncard__body"
          style={{
            height: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink-400)'
          }}
        >
          {t.pfSelezionaUnaPiuUnitGiornaliero}
        </div>
      </div>
    )
  }

  return (
    <div className="ncard" style={{ marginTop: 16 }}>
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-chart-line" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.pfAndamentoGiornaliero}</h3>
          <div className="ncard__sub">{t.pfBarreEsecuzioniLinee}</div>
        </div>
        <div className="ncard__head-actions">
          {selectedUnits.map((unit, i) => (
            <span key={unit} className="nchip">
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: getChartColor(i)
                }}
              />
              {unit}
            </span>
          ))}
        </div>
      </div>
      <div className="ncard__body">
        <Flex fw style={{ height: 380 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ ...CHART_MARGIN, right: 16 }}>
                <CartesianGrid {...CHART_GRID_PROPS} />
                <XAxis
                  dataKey="name"
                  tick={CHART_AXIS_TICK}
                  axisLine={CHART_AXIS_LINE}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  yAxisId="left"
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  label={{
                    value: t.pfEsecuzioni,
                    angle: -90,
                    position: 'insideLeft',
                    fontSize: 11,
                    fill: 'var(--ink-400)'
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatMs(v, true)}
                  label={{
                    value: t.pfTempoMedioMs,
                    angle: 90,
                    position: 'insideRight',
                    fontSize: 11,
                    fill: 'var(--ink-400)'
                  }}
                />
                <Tooltip content={<DailyComboTooltip units={selectedUnits} />} />
                <Legend wrapperStyle={CHART_LEGEND_STYLE} iconType="circle" iconSize={8} />
                <Bar
                  yAxisId="left"
                  dataKey="totalCount"
                  name={t.pfEsecuzioniSigmaUnit}
                  fill="var(--surface-3)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                  animationDuration={CHART_ANIMATION_FAST.duration}
                />
                {selectedUnits.map((unit, i) => {
                  const color = getChartColor(i)
                  const avgKey = `${unit}_avg`
                  const stdKey = `${unit}_std`
                  return (
                    <Line
                      key={unit}
                      yAxisId="right"
                      type="monotone"
                      dataKey={avgKey}
                      name={`${unit} (${t.pfMedia})`}
                      stroke={color}
                      strokeWidth={2.5}
                      dot={{ r: 3, strokeWidth: 2, stroke: 'var(--surface)', fill: color }}
                      activeDot={CHART_ACTIVE_DOT(color)}
                      connectNulls={false}
                      animationDuration={CHART_ANIMATION.duration}
                    >
                      <ErrorBar
                        dataKey={stdKey}
                        width={5}
                        stroke={color}
                        strokeWidth={1.2}
                        direction="y"
                      />
                    </Line>
                  )
                })}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <Flex fw fh style={{ justifyContent: 'center', alignItems: 'center' }}>
              <Text
                value={t.pfNessunDatoGiornaliero}
                size={14}
                style={{ color: 'var(--ink-400)' }}
              />
            </Flex>
          )}
        </Flex>
      </div>
    </div>
  )
}
